import Button, { PrimaryButton } from 'components/Button/src/jsx/Button';
import { WarningInfobox } from 'components/Infobox/src/jsx/Infobox';
import PropTypes from 'prop-types';
import { useRegistration } from '../hooks/useRegistration';
import { useScene } from '../hooks/useScene';
import Form from './Form';
import FormContent from './FormContent';
import Heading from './Heading';
import Navigation from './Navigation';
import Sso from './Sso';
import Input from './ui/Input';
import Password from './ui/Password';
import Switch from './ui/Switch';

export default function Registration({ data, getLabel, getRecaptchaToken, onFormSubmit, region, setSceneData, setError, isMobile }) {
	useScene('registration', { data, setSceneData });

	const { registrationStep, formFields, fieldIsValid, getFieldErrorMessages, handleFieldChange, nextStep, previousStep } = useRegistration({
		data,
		getLabel,
		onFormSubmit,
	});

	const sharedFieldProps = {
		fieldIsValid,
		getFieldErrorMessages,
		onChange: handleFieldChange,
		validation: undefined,
	};

	return (
		<>
			<Heading
				keyProp={registrationStep.toString()}
				title={getLabel('registration_heading')}
				subtitle={registrationStep === 0 ? getLabel('registration_subtitle') : ''}
			/>
			<Form step={registrationStep}>
				<FormContent step={registrationStep}>
					{registrationStep === 0 && (
						<>
							{isMobile && (
								<WarningInfobox small={true}>
									<p>{getLabel('registration_mobile_warning')}</p>
								</WarningInfobox>
							)}
							<Sso
								getLabel={getLabel}
								getRecaptchaToken={getRecaptchaToken}
								locale={data.locale}
								region={region}
								setError={setError}
								ssoGoogleUrl={data.apiRoutes.ssoGoogle}
								ssoMicrosoftUrl={data.apiRoutes.ssoMicrosoft}
							/>
							<p className="form__separator h6">{getLabel('registration_sso_spacer')}</p>
						</>
					)}

					<div className="form__fields">
						{formFields
							.filter((field) => field.step === registrationStep)
							.map((field) => {
								const props = {
									...sharedFieldProps,
									...field,
								};
								switch (field.type) {
									case 'switch':
										return <Switch key={field.name} {...props} />;
									case 'password':
										return <Password key={field.name} {...props} getLabel={getLabel} />;
									default:
										return <Input key={field.name} {...props} type={field.type} />;
								}
							})}
					</div>
				</FormContent>
			</Form>
			<Navigation keyProp={registrationStep.toString()}>
				<PrimaryButton onClick={nextStep} testId="nav-continue">
					{getLabel(registrationStep === 0 ? 'nav_btn_continue' : 'nav_btn_register')}
				</PrimaryButton>
				{registrationStep > 0 && (
					<Button onClick={previousStep} testId="nav-back">
						{getLabel('nav_btn_back')}
					</Button>
				)}
			</Navigation>
		</>
	);
}

Registration.propTypes = {
	data: PropTypes.object.isRequired,
	getLabel: PropTypes.func.isRequired,
	getRecaptchaToken: PropTypes.func.isRequired,
	isMobile: PropTypes.bool.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	region: PropTypes.string.isRequired,
	setError: PropTypes.func.isRequired,
	setSceneData: PropTypes.func.isRequired,
};
